import {GQLResponse, GQLSubscribeRequests} from "../gqlModel/types";
import {action, computed, makeObservable, observable} from 'mobx';

import {IGraphQLSubscriptionClient} from "../gqlModel/IGraphQLSubscriptionClient";
import {ModelSortDirection} from "../../API";
import {ColId} from "../../ui/components/Table";
import {Cancelable, debounce} from "../../utils/debounce";
import {logErrorToSentry} from "../../utils/errors/logErrorToSentry";
import {sortByField} from "../../utils/sortByField";
import {BasicDataSourceDeps} from "../../platform/services/types/DataSourceDependencies";
import {DataSourcePersistence} from "./DataSourcePersistence";


export type FilterType = 'server' | 'client';

export interface FilterDefinition<T> {
    type: FilterType;
    apply: (row: T) => boolean, /* required even for server filter as a fallback on case when we have all data loaded */
}

export interface SortRule {
    field: string;
    direction: ModelSortDirection;
}

export type FilterSchema<T> = Record<ColId, FilterDefinition<T>>;


export abstract class DataSource<
    T extends Record<string, any> = any,
    D extends Record<string, any> = T,
    DEPS extends BasicDataSourceDeps = BasicDataSourceDeps
> {
    // Constants & Configuration
    protected NO_FILTER_HASH = '';

    // Observable State
    public isFullyLoaded: boolean = false;
    public isInitialized: boolean = false;
    public isLoading: boolean = false;
    public isLoadingMore: boolean = false;
    public isRefreshing: boolean = false;
    public isShowingCachedData: boolean = false;
    public filterValues: Record<ColId, any>;
    public sortRule?: SortRule = undefined;
    public _allRows: D[] = [];

    // Internal State
    protected nextToken?: string | null = null;
    protected _isLoadingAllRows: boolean = false;
    protected _isDestroyed: boolean = false;
    protected _initialLoadPromise: Promise<void> | null = null;
    protected _latestRequestedFilterHash?: string;
    protected _latestRequestedAbortController?: AbortController;

    // Abstract Properties
    abstract readonly _modelName: string;
    protected readonly _subscriptionGQLs?: GQLSubscribeRequests;

    // Services & Deps
    protected deps: DEPS;
    protected persistence: DataSourcePersistence<T, D>;
    private _wsClient?: IGraphQLSubscriptionClient;

    // Callbacks
    public onItemUpdate?: (item: T) => void;
    public onItemDelete?: (item: T) => void;
    public onItemCreate?: (item: T) => void;

    // Debounced Methods
    protected _loadAllRowsDebounced: (() => void) & Cancelable;
    protected _applyServerFilterDebounced: (() => void) & Cancelable;
    protected _persistRealtimeCacheDebounced: (() => void) & Cancelable;

    constructor(deps: DEPS) {
        this.deps = deps;
        this.filterValues = this.getFilterDefaultValues();

        this.persistence = new DataSourcePersistence({
            getModelName: () => this._modelName,
            getSyncHash: () => this.syncHash,
            getAuthService: () => this.deps.authService,
            getServerFilterHash: () => this._getFilterHash(this.filterValues, 'server'),
            mapServerResponseToLocalRow: (row: T) => this.mapServerResponseToLocalRow(row)
        });

        makeObservable(this, {
            filterValues: observable,
            isLoading: observable,
            isLoadingMore: observable,
            isRefreshing: observable,
            isShowingCachedData: observable,
            isInitialized: observable,
            isFullyLoaded: observable,
            sortRule: observable,
            _allRows: observable,

            setFilterValue: action,
            resetFilters: action,
            setIsLoading: action,
            setIsRefreshing: action,
            setIsShowingCachedData: action,
            setIsLoadingMore: action,
            setIsInitialized: action,
            setSortRule: action,
            _setAllRows: action,

            filteredRows: computed,
            allRows: computed,
        });

        this._loadAllRowsDebounced = debounce(this._loadAllRows.bind(this));
        this._applyServerFilterDebounced = debounce(this._applyServerFilter.bind(this));
        this._persistRealtimeCacheDebounced = debounce(this._persistRealtimeCache.bind(this), 500);
    }

    /*=========================================== Abstract & Config =============================================*/

    abstract getWsClient(): Promise<IGraphQLSubscriptionClient | undefined>;

    abstract loadGQLFetch(filterValues: Record<ColId, any>): Promise<GQLResponse<T>>;

    abstract loadMoreGQLFetch(nextToken: string, filterValues: Record<ColId, any>): Promise<GQLResponse<T>>;

    abstract get syncHash(): string;

    get syncQueryFilter() {
        return {};
    }

    getFilterSchema(): FilterSchema<D> {
        return {};
    }

    getFilterDefaultValues(): Record<ColId, any> {
        return {};
    }

    /*=========================================== Public API =============================================*/

    get allRows() {
        return this._allRows;
    }

    get filteredRows() {
        let resultRows = this.allRows.slice();
        const filterSchema = this.getFilterSchema();
        for (const colId in filterSchema) {
            if (this.filterValues[colId] !== null && this.filterValues[colId] !== undefined) {
                resultRows = resultRows.filter(filterSchema[colId].apply);
            }
        }
        if (this.sortRule) {
            resultRows = sortByField(resultRows, this.sortRule.field, this.sortRule.direction, false, this.getFilterableValue.bind(this));
        }
        return resultRows;
    }

    async initialLoad(loadAllRows = true) {
        if (this._initialLoadPromise === null) {
            this._initialLoadPromise = this._initialLoad(loadAllRows);
        }
        return this._initialLoadPromise;
    }

    async loadMore() {
        if (!this.nextToken || this.isLoadingMore) {
            return false;
        }
        this.setIsLoadingMore(true);
        const ourRequestAbortController = new AbortController();
        this._cancelRequestIfPresent(ourRequestAbortController);

        const onAbort = () => {
            this.setIsLoadingMore(false);
        };
        ourRequestAbortController.signal.addEventListener('abort', onAbort);

        try {
            const response = await this.loadMoreGQLFetch(this.nextToken, this.filterValues);
            if (!ourRequestAbortController.signal.aborted) {
                this.nextToken = response.nextToken;
                const newItems = response.items.map(this.mapServerResponseToLocalRow.bind(this));
                this._setAllRows([...this._allRows, ...newItems]);
                this.persistence.setRawData([...this.persistence.getRawData(), ...response.items]);
                this.setIsLoadingMore(false);
            }
        } catch (error: any) {
            if (!ourRequestAbortController.signal.aborted) {
                this.setIsLoadingMore(false);
            }
            console.error(error);
        } finally {
            ourRequestAbortController.signal.removeEventListener('abort', onAbort);
        }
        return true;
    }

    setFilterValue(colId: string, value: any) {
        const filterSchema = this.getFilterSchema();
        if (filterSchema[colId]) {
            const filterDefinition = filterSchema[colId];
            
            if (filterDefinition.type === 'server') {
                const oldServerFilterHash = this._getFilterHash(this.filterValues, 'server');
                const newFilterValues = { ...this.filterValues, [colId]: value };
                const newServerFilterHash = this._getFilterHash(newFilterValues, 'server');
                
                if (oldServerFilterHash !== newServerFilterHash) {
                    void this.persistence.invalidate();
                    this.persistence.reset();
                }
                
                if (!this.isFullyLoaded) {
                    this._applyServerFilterDebounced();
                }
            } else if (filterDefinition.type === 'client' && !this.isFullyLoaded) {
                this._loadAllRowsDebounced();
            }

            this.filterValues[colId] = value;
        } else {
            throw new Error(`filter for ${colId} is not supported`);
        }
    }

    resetFilters() {
        const oldServerFilterHash = this._getFilterHash(this.filterValues, 'server');
        const newFilterValues = this.getFilterDefaultValues();
        const newServerFilterHash = this._getFilterHash(newFilterValues, 'server');
        
        if (oldServerFilterHash !== newServerFilterHash) {
            void this.persistence.invalidate();
            this.persistence.reset();
        }
        
        this.filterValues = newFilterValues;
    }

    setSortRule(field: string, direction: ModelSortDirection = ModelSortDirection.ASC) {
        this.sortRule = { field, direction };
    }

    toggleSort = (field: string) => {
        let targetDirection = ModelSortDirection.ASC;
        if (this.sortRule?.field === field) {
            targetDirection = this.sortRule.direction === ModelSortDirection.DESC
                ? ModelSortDirection.ASC
                : ModelSortDirection.DESC;
        }
        this.setSortRule(field, targetDirection);
    }

    destroy() {
        this._isDestroyed = true;
        this._loadAllRowsDebounced.clear();
        this._applyServerFilterDebounced.clear();
        this._persistRealtimeCacheDebounced.clear();

        if (this._wsClient) {
            this._wsClient.cancelSubscription(`onCreate${this._modelName}${this.syncHash}`);
            this._wsClient.cancelSubscription(`onUpdate${this._modelName}${this.syncHash}`);
            this._wsClient.cancelSubscription(`onDelete${this._modelName}${this.syncHash}`);
        }
    }

    /*=========================================== Internal State Management =============================================*/

    _setAllRows(_allRows: D[]) {
        this._allRows = _allRows.filter(row => (row as any)._deleted !== true);
    }

    setIsLoading(isLoading: boolean) { this.isLoading = isLoading; }
    setIsRefreshing(isRefreshing: boolean) { this.isRefreshing = isRefreshing; }
    setIsShowingCachedData(isShowingCachedData: boolean) { this.isShowingCachedData = isShowingCachedData; }
    setIsLoadingMore(isLoadingMore: boolean) { this.isLoadingMore = isLoadingMore; }
    setIsInitialized(isInitialized: boolean) { this.isInitialized = isInitialized; }

    fixNO_FILTER_HASH(filterValues: Record<string, any>) {
        this.NO_FILTER_HASH = this._getFilterHash(filterValues);
    }

    getFilterableValue = (field: string, obj: D): string | number | boolean | null | undefined => {
        return obj[field];
    };

    mapServerResponseToLocalRow(row: T): D {
        return row as unknown as D;
    }

    /*=========================================== Data Fetching Lifecycle =============================================*/

    private async _initialLoad(loadAllRows = true) {
        try {
            this.NO_FILTER_HASH = this._getFilterHash(this.getFilterDefaultValues(), 'server');
            await this.persistence.validateSession();
            
            // 1. Try Cache First
            const cached = await this.persistence.load();
            if (cached && cached.items.length > 0) {
                this.setIsShowingCachedData(true);
                // Cached pagination cursor can be stale; always rely on fresh server cursor.
                this.setNextToken(undefined);
                this._setAllRows(cached.items);
                this.setIsInitialized(true);
                
                // 2. Background Refresh & Sync
                void this._backgroundServerSync(loadAllRows)
                    .finally(() => this._initSync());
                
                return;
            }
            
            // 3. No Cache -> Regular Load
            const result = await this._applyServerFilter();
            if (result) {
                this.setIsInitialized(true);
                if (loadAllRows) {
                    await this._loadAllRows();
                }
                this._initSync();
            }
        } catch (error: any) {
            logErrorToSentry(error);
            this._initialLoadPromise = null;
        }
    }

    /**
     * Performs server sync in background after showing cached data
     */
    private async _backgroundServerSync(loadAllRows: boolean = true): Promise<void> {
        try {
            this.setIsRefreshing(true);
            const result = await this._applyServerFilter();
            if (result && loadAllRows) {
                await this._loadAllRows();
            }
            this.setIsShowingCachedData(false);
            this.setIsRefreshing(false);
        } catch (error: any) {
            this.setIsRefreshing(false);
            console.warn(`Background sync failed for ${this._modelName}:`, error);
            logErrorToSentry(error);
        }
    }

    async _applyServerFilter() {
        const currentRequestHash = this._getFilterHash();
        if (this.isLoading && currentRequestHash === this._latestRequestedFilterHash) {
            return false;
        }

        this.setIsLoading(true);
        const ourRequestAbortController = new AbortController();
        this._cancelRequestIfPresent(ourRequestAbortController);
        
        const onAbort = () => this.setIsLoading(false);
        ourRequestAbortController.signal.addEventListener('abort', onAbort);
        
        this._latestRequestedFilterHash = currentRequestHash;

        try {
            const response = await this.loadGQLFetch(this.filterValues);
            if (!ourRequestAbortController.signal.aborted) {
                this.nextToken = response.nextToken;
                this._setAllRows(response.items.map(this.mapServerResponseToLocalRow.bind(this)));
                this.persistence.setRawData(response.items);

                if (!this.nextToken) {
                    await this.persistence.save(this.persistence.getRawData(), null, false);
                }

                this.persistence.hasDataFromCache = false;
                this.setIsShowingCachedData(false);

                if (currentRequestHash === this.NO_FILTER_HASH && this.nextToken === null) {
                    this._pinFullyLoaded();
                }
            }
            if (!ourRequestAbortController.signal.aborted) {
                this.setIsLoading(false);
                this._latestRequestedAbortController = undefined;
            }
        } catch (error: any) {
            if (!ourRequestAbortController.signal.aborted) {
                this.setIsLoading(false);
            }
            console.error(error);
            this._latestRequestedFilterHash = undefined;
            throw error;
        } finally {
            ourRequestAbortController.signal.removeEventListener('abort', onAbort);
        }
        return true;
    }

    async _loadAllRows() {
        if (this.isFullyLoaded) return true;
        
        const currentRequestHash = this._getFilterHash();
        if (!this.isInitialized || (this._isLoadingAllRows && currentRequestHash === this._latestRequestedFilterHash)) {
            return false;
        }

        this._isLoadingAllRows = true;
        this._latestRequestedFilterHash = currentRequestHash;

        const ourRequestAbortController = new AbortController();
        this._cancelRequestIfPresent(ourRequestAbortController);
        this.setIsLoadingMore(true);

        const onAbort = () => {
            this.setIsLoadingMore(false);
            this._isLoadingAllRows = false;
        };
        ourRequestAbortController.signal.addEventListener('abort', onAbort);

        try {
            let accumulatedRows = [...this._allRows];
            let accumulatedRawData = [...this.persistence.getRawData()];
            
            while (this.nextToken) {
                const response = await this.loadMoreGQLFetch(this.nextToken, this.filterValues);
                if (ourRequestAbortController.signal.aborted) break;
                
                this.nextToken = response.nextToken;
                accumulatedRows = [...accumulatedRows, ...response.items.map(this.mapServerResponseToLocalRow.bind(this))];
                accumulatedRawData = [...accumulatedRawData, ...response.items];
            }

            if (!ourRequestAbortController.signal.aborted) {
                this._setAllRows(accumulatedRows);
                this.persistence.setRawData(accumulatedRawData);
                await this.persistence.save(this.persistence.getRawData(), this.nextToken, !!this.nextToken);
                
                this.persistence.hasDataFromCache = false;
                this._isLoadingAllRows = false;
                this.setIsLoadingMore(false);
                
                if (currentRequestHash === this.NO_FILTER_HASH) {
                    this._pinFullyLoaded();
                }
            }
        } catch (error: any) {
            if (!ourRequestAbortController.signal.aborted) {
                this.setIsLoadingMore(false);
                this._isLoadingAllRows = false;
            }
            console.error(error);
        } finally {
            ourRequestAbortController.signal.removeEventListener('abort', onAbort);
        }
        return true;
    }

    _pinFullyLoaded() {
        this.isFullyLoaded = true;
    }

    _cancelRequestIfPresent(newAbortController: AbortController) {
        if (this._latestRequestedAbortController) {
            this._latestRequestedAbortController.abort();
        }
        this._latestRequestedAbortController = newAbortController;
    }

    public get nextTokenValue(): string | null | undefined {
        return this.nextToken;
    }

    public setNextToken(token: string | null | undefined) {
        this.nextToken = token;
    }

    public setPersistenceEnabled(enabled: boolean) {
        this.persistence.setEnabled(enabled);
    }

    /*=========================================== Synchronization (WS) =============================================*/

    async _initSync() {
        if (!this._subscriptionGQLs) return;
        this._wsClient = await this.getWsClient();
        if (this._wsClient && !this._isDestroyed) {
            this._wsClient.registerSubscription(`onCreate${this._modelName}${this.syncHash}`, this._subscriptionGQLs.onCreate, this._onCreate, this.syncQueryFilter);
            this._wsClient.registerSubscription(`onUpdate${this._modelName}${this.syncHash}`, this._subscriptionGQLs.onUpdate, this._onUpdate, this.syncQueryFilter);
            this._wsClient.registerSubscription(`onDelete${this._modelName}${this.syncHash}`, this._subscriptionGQLs.onDelete, this._onDelete, this.syncQueryFilter);
        }
    }

    protected _upsertItem(item: T, toStart: boolean = false) {
        if (this._isDestroyed) return;

        const mappedItem = this.mapServerResponseToLocalRow(item);

        // Update Observable Rows
        const updatedRows = [...this._allRows];
        const index = updatedRows.findIndex(row => row.id === item.id);
        if (index === -1) {
            toStart ? updatedRows.unshift(mappedItem) : updatedRows.push(mappedItem);
        } else {
            updatedRows[index] = mappedItem;
        }
        this._setAllRows(updatedRows);

        // Update Raw Data via Persistence
        this.persistence.upsertItem(item, toStart);

        this._persistRealtimeCacheDebounced();
    }

    protected _removeItem(id: string) {
        if (this._isDestroyed) return;

        const initialLength = this._allRows.length;
        this._setAllRows(this._allRows.filter(item => item.id !== id));
        
        if (this.persistence.removeItem(id) || (this._allRows.length !== initialLength)) {
            this._persistRealtimeCacheDebounced();
        }
    }

    _onCreate = (createdItem: T) => {
        if (!createdItem) return;
        this._upsertItem(createdItem, true);
        if (this.onItemCreate) this.onItemCreate(createdItem);
    }

    _onUpdate = (updatedItem: T) => {
        if (!updatedItem) return;
        this._upsertItem(updatedItem);
        if (this.onItemUpdate) this.onItemUpdate(updatedItem);
    }

    _onDelete = (deletedItem: T) => {
        if (!deletedItem) return;
        this._removeItem(deletedItem.id);
        if (this.onItemDelete) this.onItemDelete(deletedItem);
    }

    private _persistRealtimeCache = async () => {
        if (this.nextToken !== null) return;
        await this.persistence.save(this.persistence.getRawData(), null, false);
    }

    /*=========================================== Helpers =============================================*/

    _getFilterHash(filterValues = this.filterValues, type: 'server' | 'client' | 'all' = 'server') {
        let filterHash = '';
        const filterSchema = this.getFilterSchema();

        for (const colId in filterSchema) {
            const schema = filterSchema[colId];
            const value = filterValues[colId];
            if (value == null) continue;

            if ((type === 'server' && schema.type === 'server') ||
                (type === 'client' && schema.type === 'client') ||
                (type === 'all')) {
                filterHash += `${colId}#${JSON.stringify(value)}`;
            }
        }
        return filterHash;
    }
}
